import { useState, useMemo, useCallback } from 'react'
import Fuse from 'fuse.js'
import type { InventoryItemModel } from '@itemize/server/models'

export type SortOption = 'name' | 'price' | 'updatedAt' | 'createdAt'
export type SortOrder = 'asc' | 'desc'

export interface UseInventorySearchAndSortOptions {
  items?: InventoryItemModel[]
  defaultSortBy?: SortOption
  defaultSortOrder?: SortOrder
}

export const useInventorySearchAndSort = ({
  items = [],
  defaultSortBy = 'updatedAt',
  defaultSortOrder = 'desc',
}: UseInventorySearchAndSortOptions = {}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>(defaultSortBy)
  const [sortOrder, setSortOrder] = useState<SortOrder>(defaultSortOrder)

  // Configure Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    if (!items || items.length === 0) return null
    return new Fuse(items, {
      keys: ['name', 'description'],
      threshold: 0.3,
      includeScore: true,
    })
  }, [items])

  // Filter and sort items
  const filteredAndSortedItems = useMemo(() => {
    if (!items || items.length === 0) return []

    let filteredItems = items

    // Apply search filter
    if (searchQuery.trim() && fuse) {
      const searchResults = fuse.search(searchQuery.trim())
      filteredItems = searchResults.map((result) => result.item)
    }

    // Apply sorting
    const sortedItems = [...filteredItems].sort((a, b) => {
      let comparison = 0

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'price':
          comparison = a.priceCents - b.priceCents
          break
        case 'updatedAt':
          comparison = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          break
        case 'createdAt':
          comparison = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          break
        default:
          comparison = 0
      }

      // Apply sort order
      if (sortBy === 'name' || sortBy === 'price') {
        return sortOrder === 'asc' ? comparison : -comparison
      } else {
        // For date fields, default desc means newest first, so we reverse for asc
        return sortOrder === 'desc' ? comparison : -comparison
      }
    })

    return sortedItems
  }, [items, searchQuery, sortBy, sortOrder, fuse])

  const resetSearch = useCallback(() => {
    setSearchQuery('')
  }, [])

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
  }

  return {
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filteredAndSortedItems,
    resetSearch,
    toggleSortOrder,
  }
}
